import { useState, useCallback, useRef } from "react";
import { useOptions } from "./use-optionsData";
import RuleEngineBackend from "../../services/RuleEngineBackend";
import PPUtils from "../../../utils/PPUtils";

export const useCategoryData = () => {
     const { getCategoryOptions } = useOptions();
     const categoryOptions = getCategoryOptions();

     const [dynamicOptions, setDynamicOptions] = useState({
          areas: [],
          categories: [],
          articles: [],
          manufacturers: []
     });

     const [loadingStates, setLoadingStates] = useState({
          areas: false,
          categories: false,
          articles: false,
          manufacturers: false
     });
     const requestVersions = useRef({
          areas: 0,
          categories: 0,
          articles: 0,
          manufacturers: 0
     });

     const setLoading = useCallback((type, isLoading) => {
          setLoadingStates(prev => ({
               ...prev,
               [type]: isLoading
          }));
     }, []);

     const loadAreas = useCallback(async (selectedDepartments) => {
          const requestVersion = ++requestVersions.current.areas;
          if (!Array.isArray(selectedDepartments) || selectedDepartments.length === 0) {
               setDynamicOptions(prev => ({ ...prev, areas: [], categories: [], articles: [] }));
               return [];
          }

          const request = PPUtils.getCustomerRequestContext();
          if (request == null) {
               setDynamicOptions(prev => ({ ...prev, areas: [], categories: [], articles: [] }));
               return [];
          }

          setLoading('areas', true);
          try {
               const { token, customerId } = request;

               const allDepOptionsElement = selectedDepartments.find(dept => dept.isAllOption);
               const actualDepartments = allDepOptionsElement ? allDepOptionsElement.allEntries : selectedDepartments  
               const departmentCatShorts = actualDepartments.map(dept => dept.catShort);

               const level2Categories = await RuleEngineBackend.getLevel2Categories(token, customerId, departmentCatShorts);
               if (requestVersion !== requestVersions.current.areas) {
                    return [];
               }
               
               if (level2Categories && Array.isArray(level2Categories)) {
                    const formattedAreas = level2Categories.map(cat => ({
                         value: cat.id,
                         label: cat.name,
                         catShort: cat.catShort
                    }));

                    const areasWithAll = [
                         { value: 'all_areas', label: '- Alle Bereiche -', catShort: 'all', isAllOption: true, allEntries: formattedAreas},
                         ...formattedAreas
                    ];

                    setDynamicOptions(prev => ({ 
                         ...prev, 
                         areas: areasWithAll,
                         categories: [],
                         articles: []
                    }));
                    return areasWithAll;
               } else {
                    setDynamicOptions(prev => ({ ...prev, areas: [], categories: [], articles: [] }));
                    return [];
               }
          } catch (error) {
               console.error('Error loading areas:', error);
               if (requestVersion === requestVersions.current.areas) {
                    setDynamicOptions(prev => ({ ...prev, areas: [], categories: [], articles: [] }));
               }
               return [];
          } finally {
               if (requestVersion === requestVersions.current.areas) {
                    setLoading('areas', false);
               }
          }
     }, [setLoading]);

     const loadCategories = useCallback(async (selectedAreas) => {
          const requestVersion = ++requestVersions.current.categories;
          if (!Array.isArray(selectedAreas) || selectedAreas.length === 0) {
               setDynamicOptions(prev => ({ ...prev, categories: [], articles: [] }));
               return [];
          }

          const request = PPUtils.getCustomerRequestContext();
          if (request == null) {
               setDynamicOptions(prev => ({ ...prev, categories: [], articles: [] }));
               return [];
          }

          setLoading('categories', true);
          try {
               const { token, customerId } = request;

               const allAreasOptionsElement = selectedAreas.find(dept => dept.isAllOption);
               const actualAreas = allAreasOptionsElement ? allAreasOptionsElement.allEntries : selectedAreas  
               const areaCatShorts = actualAreas.map(dept => dept.catShort);
               
               const level3Categories = await RuleEngineBackend.getLevel3Categories(token, customerId, areaCatShorts);
               if (requestVersion !== requestVersions.current.categories) {
                    return [];
               }
               
               if (level3Categories && Array.isArray(level3Categories)) {
                    const formattedCategories = level3Categories.map(cat => ({
                         value: cat.id || `${cat.catShort}-${cat.lang}`,
                         label: cat.name,
                         catShort: cat.catShort,
                         lang: cat.lang
                    }));

                    const categoriesWithAll = [
                         { value: 'all_categories', label: '- Alle Kategorien -', catShort: 'all', isAllOption: true, allEntries: formattedCategories },
                         ...formattedCategories
                    ];

                    setDynamicOptions(prev => ({ 
                         ...prev, 
                         categories: categoriesWithAll,
                         articles: []
                    }));
                    return categoriesWithAll;
               } else {
                    setDynamicOptions(prev => ({ ...prev, categories: [], articles: [] }));
                    return [];
               }
          } catch (error) {
               console.error('Error loading categories:', error);
               if (requestVersion === requestVersions.current.categories) {
                    setDynamicOptions(prev => ({ ...prev, categories: [], articles: [] }));
               }
               return [];
          } finally {
               if (requestVersion === requestVersions.current.categories) {
                    setLoading('categories', false);
               }
          }
     }, [setLoading]);

     const loadArticles = useCallback(async (selectedDepartments, selectedAreas, selectedCategories, selectedProducers) => {
          const requestVersion = ++requestVersions.current.articles;
          if (!Array.isArray(selectedDepartments) || selectedDepartments.length === 0) {
               setDynamicOptions(prev => ({ ...prev, articles: [] }));
               return [];
          }

          const request = PPUtils.getCustomerRequestContext();
          if (request == null) {
               setDynamicOptions(prev => ({ ...prev, articles: [] }));
               return [];
          }

          setLoading('articles', true);
          try {
               const { token, customerId } = request;

               const actualDepartments = selectedDepartments.filter(dept => !dept.isAllOption);
               const actualAreas = Array.isArray(selectedAreas) ? selectedAreas.filter(area => !area.isAllOption) : [];
               const actualCategories = Array.isArray(selectedCategories) ? selectedCategories.filter(cat => !cat.isAllOption) : [];


               const departmentCatShorts = actualDepartments.map(dept => dept.catShort);
               const areaCatShorts = actualAreas.map(area => area.catShort);
               const categoryCatShorts = actualCategories.map(cat => cat.catShort);
               
               const producers = Array.isArray(selectedProducers)
                    ? selectedProducers
                         .filter(p => p && !p.isAllOption)
                         .map(p => p.name || p.label || p.value)
                         .filter(Boolean)
                    : [];

               const categoryTree = await RuleEngineBackend.getCategoryTree(
                    token, 
                    customerId, 
                    'de',
                    departmentCatShorts,
                    areaCatShorts,
                    categoryCatShorts,
                    producers
               );
               if (requestVersion !== requestVersions.current.articles) {
                    return [];
               }
               
               if (categoryTree && Array.isArray(categoryTree)) {
                    const formattedArticles = categoryTree.map(product => ({
                         value: product.id,
                         label: `${product.productName} (${product.productnr})`,
                         productName: product.productName,
                         productnr: product.productnr,
                         productProducer: product.productProducer,
                         department: product.department,
                         productGroup: product.productGroup,
                         productCat: product.productCat
                    }));

                    const articlesWithAll = [
                         { value: 'all_articles', label: '- Alle Artikel -', isAllOption: true, allEntries: formattedArticles },
                         ...formattedArticles
                    ];

                    setDynamicOptions(prev => ({ ...prev, articles: articlesWithAll }));
                    return articlesWithAll;
               } else {
                    setDynamicOptions(prev => ({ ...prev, articles: [] }));
                    return [];
               }
          } catch (error) {
               console.error('Error loading articles:', error);
               if (requestVersion === requestVersions.current.articles) {
                    setDynamicOptions(prev => ({ ...prev, articles: [] }));
               }
               return [];
          } finally {
               if (requestVersion === requestVersions.current.articles) {
                    setLoading('articles', false);
               }
          }
     }, [setLoading]);

     const loadManufacturers = useCallback(async () => {
          const requestVersion = ++requestVersions.current.manufacturers;
          const request = PPUtils.getCustomerRequestContext();
          if (request == null) {
               setDynamicOptions(prev => ({ ...prev, manufacturers: [] }));
               return [];
          }
          setLoading('manufacturers', true);
          try {
               const { token, customerId } = request;
               const producers = await RuleEngineBackend.getProducers(token, customerId);
               if (requestVersion !== requestVersions.current.manufacturers) {
                    return [];
               }

               if (producers && Array.isArray(producers)) {
                    const formattedManufacturers = producers
                         .filter((producer) =>
                              producer &&
                              String(producer).trim() !== "" &&
                              String(producer).trim().toUpperCase() !== "DEFAULT"
                         )
                         .map((manufacturer) => ({
                              value: manufacturer,
                              label: manufacturer,
                              name: manufacturer
                         }));

                    const manufacturersWithAll = [
                         { value: 'all_manufacturers', label: '- Alle Hersteller -', isAllOption: true, allEntries: formattedManufacturers },
                         ...formattedManufacturers
                    ];

                    setDynamicOptions(prev => ({ ...prev, manufacturers: manufacturersWithAll }));
                    return manufacturersWithAll;
               } else {
                    setDynamicOptions(prev => ({ ...prev, manufacturers: [] }));
                    return [];
               }
          } catch (error) {
               console.error('Error loading manufacturers:', error);
               if (requestVersion === requestVersions.current.manufacturers) {
                    setDynamicOptions(prev => ({ ...prev, manufacturers: [] }));
               }
               return [];
          } finally {
               if (requestVersion === requestVersions.current.manufacturers) {
                    setLoading('manufacturers', false);
               }
          }
     }, [setLoading]);

     return {
          departmentOptions: [
               { value: 'all_departments', label: '- Alle Abteilungen -', catShort: 'all', isAllOption: true, allEntries: categoryOptions.departments },
               ...categoryOptions.departments
          ],
          
          areaOptions: dynamicOptions.areas,
          categoryOptions: dynamicOptions.categories,
          articleOptions: dynamicOptions.articles,
          manufacturerOptions: dynamicOptions.manufacturers,
          loadingStates,
          loadAreas,
          loadCategories,
          loadArticles,
          loadManufacturers
     };
};
